import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Loader2, ChevronDown, ChevronUp, Sparkles, Power, Wallet, PencilLine, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useFmt } from '../traffic-manager/tmShared';

export interface SyncChange {
  campaign_id: string;
  name: string;
  platform: string;
  kind: 'new' | 'status' | 'budget' | 'name' | 'spend';
  from?: any;
  to?: any;
}

export interface SyncFreshness {
  last_full_sync_at: string | null;
  last_trigger: 'manual' | 'stale' | 'auto' | null;
  last_status: string | null;
  last_errors: string[] | null;
  metrics_synced_at: string | null;
  stale: boolean;
  stale_after_min: number;
  running: { jobId: string; progress: any; trigger?: 'manual' | 'stale' | 'auto' } | null;
  last_changes: SyncChange[];
  last_spend_changes: number;
  last_summary: Record<SyncChange['kind'], number>;
}

const PLATAFORMA: Record<string, string> = { google: 'Google Ads', meta: 'Meta Ads', linkedin: 'LinkedIn Ads', openai: 'ChatGPT Ads' };
const ICONE: Record<SyncChange['kind'], React.ElementType> = { new: Sparkles, status: Power, budget: Wallet, name: PencilLine, spend: Wallet };

/**
 * Faixa "quão novos são estes dados": última atualização completa (status,
 * orçamento, campanhas novas) e das métricas, se está atualizando agora, e o
 * que mudou na última vez, para conferir sem abrir campanha por campanha.
 */
const SyncFreshnessBar: React.FC<{ f: SyncFreshness | null; syncing: boolean; onSyncNow: () => void; currency: string }> = ({ f: s, syncing, onSyncNow, currency }) => {
  const { t } = useTranslation();
  const f = useFmt();
  const [aberto, setAberto] = useState(false);
  if (!s) return null;
  const mudancas = s.last_changes || [];
  const st = (x: any) => t(`campaigns.status.${x}`, String(x || '—'));
  const descricao = (c: SyncChange) => {
    if (c.kind === 'new') return t('campaigns.freshness.cNew', 'campanha nova trazida da plataforma ({{s}})', { s: st(c.to) });
    if (c.kind === 'status') return t('campaigns.freshness.cStatus', 'status {{a}} → {{b}}', { a: st(c.from), b: st(c.to) });
    if (c.kind === 'budget') return t('campaigns.freshness.cBudget', 'orçamento {{a}} → {{b}}', { a: c.from != null ? f.money(c.from, currency) : '—', b: c.to != null ? f.money(c.to, currency) : '—' });
    if (c.kind === 'name') return t('campaigns.freshness.cName', 'nome mudou (era "{{a}}")', { a: c.from });
    return '';
  };
  const resumo = (['new', 'status', 'budget', 'name'] as const).filter((k) => s.last_summary && s.last_summary[k]).map((k) => {
    const n = s.last_summary[k];
    return k === 'new' ? t('campaigns.freshness.sNew', '{{n}} nova(s)', { n }) : k === 'status' ? t('campaigns.freshness.sStatus', '{{n}} status', { n }) : k === 'budget' ? t('campaigns.freshness.sBudget', '{{n}} orçamento(s)', { n }) : t('campaigns.freshness.sName', '{{n}} nome(s)', { n });
  });
  const quem = s.last_trigger === 'auto' ? t('campaigns.freshness.byAuto', 'automática') : s.last_trigger === 'stale' ? t('campaigns.freshness.byStale', 'ao abrir a tela') : t('campaigns.freshness.byManual', 'pelo botão');
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-4 py-2.5 text-sm">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {syncing || s.running
            ? <><Loader2 className="w-4 h-4 animate-spin text-primary-600 shrink-0" /><span className="text-gray-700 dark:text-gray-200">{t('campaigns.freshness.updating', 'Buscando as mudanças nas plataformas…')}</span></>
            : s.stale
              ? <><AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" /><span className="text-gray-700 dark:text-gray-200">{s.last_full_sync_at ? t('campaigns.freshness.old', 'Conferido nas plataformas {{d}}', { d: f.relative(s.last_full_sync_at) }) : t('campaigns.freshness.never', 'Status e orçamentos ainda não foram conferidos nas plataformas')}</span></>
              : <><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /><span className="text-gray-700 dark:text-gray-200">{t('campaigns.freshness.fresh', 'Conferido nas plataformas {{d}}', { d: f.relative(s.last_full_sync_at as string) })} <span className="text-gray-500">({quem})</span></span></>}
          {s.metrics_synced_at && <span className="hidden md:inline text-gray-500 truncate">· {t('campaigns.freshness.metrics', 'métricas {{d}}', { d: f.relative(s.metrics_synced_at) })}</span>}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {mudancas.length > 0 && !syncing && (
            <button type="button" onClick={() => setAberto((v) => !v)} aria-expanded={aberto} className="inline-flex items-center gap-1 text-primary-600 hover:underline">
              {t('campaigns.freshness.whatChanged', 'O que mudou')}: {resumo.join(', ')}
              {aberto ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          )}
          {!syncing && !s.running && (
            <button type="button" onClick={onSyncNow} className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-primary-600">
              <RefreshCw className="w-4 h-4" />{t('campaigns.freshness.now', 'Atualizar agora')}
            </button>
          )}
        </div>
      </div>
      {aberto && mudancas.length > 0 && (
        <ul className="border-t border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700 max-h-80 overflow-auto">
          {mudancas.map((c, i) => {
            const Icon = ICONE[c.kind] || Sparkles;
            return (
              <li key={`${c.campaign_id}-${c.kind}-${i}`} className="flex items-start gap-3 px-4 py-2 text-sm">
                <Icon className="w-4 h-4 mt-0.5 text-gray-400 shrink-0" />
                <div className="min-w-0">
                  <Link to={`/campaigns/${c.campaign_id}`} className="font-medium text-gray-900 dark:text-white hover:text-primary-600 line-clamp-1">{c.name}</Link>
                  <div className="text-xs text-gray-500">{descricao(c)} · {PLATAFORMA[c.platform] || c.platform}</div>
                </div>
              </li>
            );
          })}
          {s.last_spend_changes > 0 && <li className="px-4 py-2 text-xs text-gray-500">{t('campaigns.freshness.spendToo', 'E o gasto de {{n}} campanha(s) foi atualizado.', { n: s.last_spend_changes })}</li>}
        </ul>
      )}
    </div>
  );
};

export default SyncFreshnessBar;
